import React, { useEffect, useState } from 'react'
import { Server } from '../../../../api/Server'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ROUTES } from '../../../../router/routes'
import { useApi } from '../../../../api/api'
import { ScoopSpinner, ScoopText, ScoopButton } from '../../../ui'
import { displayPorperProductNameInDialog } from './APIUIOverrides'
import { SuccessConnectionToSlack } from './SuccessConnectionToSlack'
import { AppSumoAuthFlow } from '../../../AppSumo/AppSumoAuthFlow'
import ScoopLogo from '../../../../assets/common/ScoopLogo.svg'
import styles from './APIConnectorAuth.module.scss'

export const APIConnectorAuth = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const { postData: postDataDefault } = useApi()
    const userID = useSelector((state) => state.auth.userID)
    const loadingAuthentication = useSelector((state) => state.auth.loadingAuthentication)
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
    const token = useSelector((state) => state.auth.token)
    const [connector, setConnector] = React.useState(null)
    const [loadingAuth, setLoadingAuth] = useState(false)
    // this flow means the user came in via the setup page and now has to authorize gmail
    const [isFinishSetupFlow, setIsFinishSetupFlow] = useState(false)
    const [appSumoCode, setAppSumoCode] = useState(null)

    useEffect(() => {
        const connector = searchParams?.get('connector')
        // Allow Slack and AppSumo to work without authentication
        if (connector !== 'Slack' && connector !== 'AppSumo') {
            if (!isAuthenticated) {
                localStorage.setItem('redirectUrl', `${location.pathname}${location.search}`)
                navigate(ROUTES.LOGIN)
            }
        }
    }, [isAuthenticated])

    useEffect(() => {
        const isSlack = searchParams?.get('connector') === 'Slack'
        const isAppSumo = searchParams?.get('connector') === 'AppSumo'
        // Allow Slack and AppSumo to proceed without auth, others need token/userID
        if (!isSlack && !isAppSumo && (!token || !userID)) return
        setLoadingAuth(true)
        let queryString = window.location.search
        // Handle HTML-encoded ampersands in the URL
        queryString = queryString.replace(/&amp;/g, '&')
        let code = null
        let code_challenge = null
        let apiWorkspaceID = null
        if (queryString.indexOf('?', 1) >= 0) {
            queryString =
                queryString.substring(0, queryString.indexOf('?', 1)) +
                '&' +
                queryString.substring(queryString.indexOf('?', 1) + 1)
        }
        let queryParams = new URLSearchParams(queryString)
        let connector = queryParams.get('connector')
        setConnector(connector)
        // Special handling for AppSumo
        if (connector === 'AppSumo') {
            code = queryParams.get('code')
            // Also get the state parameter if it exists
            const state = queryParams.get('state')
            setAppSumoCode(code)
            return
        }
        let apiState = null
        let additionalParameters = []
        queryParams.forEach((value, key) => {
            if (key === 'code') {
                code = value
            } else if (key === 'state') {
                // Don't decode or interpret state - pass to backend for validation
                // Backend will validate this is a legitimate OAuth state token
                apiState = value
            } else if (key === 'code_challenge') {
                code_challenge = value
            } else if (key !== 'connector') {
                additionalParameters.push({ key: key, value: value })
            }
        })
        if (isSlack && !loadingAuthentication) {
            const decodedCode = decodeURIComponent(code)
            const decodedCodeChallenge = code_challenge ? decodeURIComponent(code_challenge) : null
            // Don't use state as workspace - backend will validate state and determine correct workspace
            // Pass userID as temporary workspace for Server object (backend will override with validated workspace)
            const newServer = new Server(userID, userID, token)
            saveAuth(
                decodedCode,
                connector,
                decodedCodeChallenge,
                newServer,
                additionalParameters,
                apiState
            )
        } else {
            if (code && token && userID) {
                const decodedCode = decodeURIComponent(code)
                const decodedCodeChallenge = code_challenge
                    ? decodeURIComponent(code_challenge)
                    : null
                const decodeConnector = decodeURIComponent(connector)
                // Backend will validate state and use the correct workspace
                // No need to pre-fetch workspace - state contains validated workspace info
                if (!apiState || apiState === 'null') {
                    // Fallback: get user's personal workspace (shouldn't normally happen with new state system)
                    const getUserDataAction = {
                        action: 'getObjects',
                        attributeName: 'userID',
                        attributeValue: userID,
                        class: 'scoop.user.User',
                    }
                    postDataDefault(getUserDataAction).then((r) => {
                        const userData = r.objects[0]
                        if (userData) {
                            const personalWorkspace = userData.personalWorkspace
                            if (!personalWorkspace) {
                                const getUserWorkspaces = {
                                    action: 'getObjects',
                                    attributeName: 'userID',
                                    class: 'scoop.workspace.Workspace',
                                    attributeValue: userID,
                                    relatedClass: 'scoop.workspace.WorkspaceUser',
                                }
                                postDataDefault(getUserWorkspaces).then((r) => {
                                    const workspaces = r.objects
                                    if (workspaces.length > 0) {
                                        const personal = workspaces[0].workspaceID
                                        setLoadingAuth(false)
                                        const newServer = new Server(personal, userID, token)
                                        saveAuth(
                                            decodedCode,
                                            connector,
                                            decodedCodeChallenge,
                                            newServer,
                                            additionalParameters,
                                            apiState
                                        )
                                    }
                                })
                            } else {
                                setLoadingAuth(false)
                                const newServer = new Server(personalWorkspace, userID, token)
                                saveAuth(
                                    decodedCode,
                                    connector,
                                    decodedCodeChallenge,
                                    newServer,
                                    additionalParameters,
                                    apiState
                                )
                            }
                        }
                    })
                } else {
                    // Use userID as temporary workspace - backend validates state and overrides with correct workspace
                    const newServer = new Server(userID, userID, token)
                    saveAuth(
                        decodedCode,
                        connector,
                        decodedCodeChallenge,
                        newServer,
                        additionalParameters,
                        apiState
                    )
                }
            } else if (queryParams.toString() !== '') {
                setIsFinishSetupFlow(false)
            }
        }
    }, [userID, token, loadingAuthentication])

    const saveAuth = (
        code,
        connector,
        decodedCodeChallenge,
        server,
        additionalParameters,
        state
    ) => {
        let action = {
            action: 'handleOAuthRedirect',
            code: code,
            state: state, // Pass state for backend validation (CRITICAL security parameter)
            connectionType: connector,
            queryParams: additionalParameters,
        }
        if (decodedCodeChallenge) action.codeChallenge = decodedCodeChallenge
        const isSlackConnector = connector?.toLowerCase() === 'slack'
        server.sheetPostData(
            action,
            (results) => {
                if (results && results.oauth && results.oauth === 'success') {
                    setIsFinishSetupFlow(true)
                    if (isSlackConnector) {
                        window.dataLayer?.push({ event: 'User_slack_signup' })
                    }
                    if (connector?.toLowerCase() === 'pipedrive') navigate(ROUTES.CANVAS)
                } else {
                    setIsFinishSetupFlow(false)
                }
                setLoadingAuth(false)
            },
            null,
            null,
            isSlackConnector
        )
    }

    // Handle AppSumo flow separately
    if (connector === 'AppSumo' && appSumoCode) {
        return <AppSumoAuthFlow code={appSumoCode} />
    }

    // Allow Slack and AppSumo to render without authentication
    if (!isAuthenticated && connector?.toLowerCase() !== 'slack' && connector !== 'AppSumo')
        return null

    if (connector?.toLowerCase() === 'slack') {
        return loadingAuth ? (
            <div className={styles.container}>
                <ScoopSpinner size={40} />
            </div>
        ) : (
            <SuccessConnectionToSlack success={isFinishSetupFlow} />
        )
    }

    return (
        <div className={styles.container}>
            <img src={ScoopLogo} alt="Scoop Logo" className={styles.logo} />
            {loadingAuth ? (
                <ScoopSpinner size={40} />
            ) : (
                <>
                    <ScoopText className={styles.title}>
                        {isFinishSetupFlow
                            ? `Successfully connected to ${displayPorperProductNameInDialog(connector)}!`
                            : `Failed to connect to ${displayPorperProductNameInDialog(connector)}`}
                    </ScoopText>
                    <div className={styles.messageCard}>
                        <ScoopText className={styles.subtitle}>
                            {isFinishSetupFlow
                                ? 'You can now close this page and return to Scoop.'
                                : 'Please verify that you have the appropriate permissions to connect to the application from Scoop.'}
                        </ScoopText>
                    </div>
                    {!isFinishSetupFlow && (
                        <div className={styles.buttonsContainer}>
                            {isAuthenticated && (
                                <ScoopButton
                                    onClick={() => navigate(ROUTES.SOURCES)}
                                    className={styles.button}
                                >
                                    Back to Datasets
                                </ScoopButton>
                            )}
                            <ScoopButton
                                onClick={() => window.location.reload()}
                                className={styles.button}
                            >
                                Try Again
                            </ScoopButton>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
